import { useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Loader2 } from 'lucide-react';

const EditTaskDialog = ({
  open,
  onOpenChange,
  formData,
  setFormData,
  llcs,
  users,
  priorityOptions,
  formLoading,
  onSubmit
}) => {
  const llcCount = formData.llc_ids?.length || 0;
  const taskCount = formData.task_ids?.length || 0;

  // Sort LLCs alphabetically
  const sortedLlcs = useMemo(() => {
    return [...llcs].sort((a, b) => a.name.localeCompare(b.name));
  }, [llcs]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="font-serif">Edit Task</DialogTitle>
          <DialogDescription>
            {taskCount > 1 
              ? `Updating ${taskCount} related tasks`
              : 'Update task details'
            }
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>
              {llcCount > 1 ? `Applies to ${llcCount} LLCs` : 'LLC'}
            </Label>
            {llcCount > 1 ? (
              <div className="border rounded-md p-2 bg-slate-50 text-sm text-slate-700 max-h-[80px] overflow-y-auto">
                {formData.llc_ids.map(id => sortedLlcs.find(l => l.id === id)?.name).filter(Boolean).join(', ')}
              </div>
            ) : (
              <Select value={formData.llc_id} onValueChange={(v) => setFormData({...formData, llc_id: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select LLC" />
                </SelectTrigger>
                <SelectContent>
                  {sortedLlcs.map((llc) => (
                    <SelectItem key={llc.id} value={llc.id}>{llc.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <div className="space-y-2">
            <Label>Assign To</Label>
            <Select value={formData.assigned_to_id} onValueChange={(v) => setFormData({...formData, assigned_to_id: v})}>
              <SelectTrigger>
                <SelectValue placeholder="Select user" />
              </SelectTrigger>
              <SelectContent>
                {users.map((user) => (
                  <SelectItem key={user.id} value={user.id}>{user.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Priority</Label>
            <Select value={formData.priority} onValueChange={(v) => setFormData({...formData, priority: v})}>
              <SelectTrigger>
                <SelectValue placeholder="Select priority" />
              </SelectTrigger>
              <SelectContent>
                {priorityOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    <div className="flex items-center gap-2">
                      <opt.icon className="w-4 h-4" />
                      {opt.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Task Description</Label>
            <Input
              value={formData.task_description}
              onChange={(e) => setFormData({...formData, task_description: e.target.value})}
              placeholder="Enter task description"
            />
          </div>
          <div className="space-y-2">
            <Label>Date Needed</Label>
            <Input
              type="date"
              value={formData.date_needed}
              onChange={(e) => setFormData({...formData, date_needed: e.target.value})}
            />
          </div>
          <div className="space-y-2">
            <Label>Date Completed</Label>
            <Input
              type="date"
              value={formData.date_completed || ''}
              onChange={(e) => setFormData({...formData, date_completed: e.target.value})}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onSubmit} disabled={formLoading}>
            {formLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {taskCount > 1 ? `Update ${taskCount} Tasks` : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditTaskDialog;
